import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
  AssignmentDetailWithEndDateTime,
  RoutePlanningActionType,
  RoutePlanningAsyncActionType,
  RoutePlanningErrorType,
  getAddressPositionWithCache,
  useRoutePlanningContext,
  useRoutePlanningDispatch
} from "../providers/RoutePlanningProvider";
import styled from "@emotion/styled";
import {Button, Callout, Icon, Intent, ProgressBar, Radio, Switch, Text} from "@blueprintjs/core";
import {COLORS, TenantPreferences} from "../constants/constants";
import JobsGrid from "../components/route-planning/JobsGrid";
import DriversGrid from "../components/route-planning/DriversGrid";
import IPManifestSelector from "../components/route-planning/IPManifestSelector";
import Alert, {IAlertRef} from "../components/common/Alert";
import {ConfirmationDialog} from "../components/common/ConfirmationDialog";
import {useHistory} from "react-router-dom";
import {WarningIcon} from "../components/common/icons";
import {LoadingSpinner} from "../components/common/LoadingSpinner";
import {useDispatchRulesDataContext} from "../providers/DispatchRulesDataProvider";
import DriversInformation, {DriversInformationRef} from "../components/route-planning/DriversInformation";
import {
  GetRoutePlanningResultResponse,
  RoutePlanningDriverInput,
  RoutePlanningMetricsInput,
  RoutePlanningUnassignedDetail,
  SubmitRoutePlanningInput,
  SubmitRoutePlanningResponse,
  useGetRoutePlanningResultLazyQuery,
  useGetRoutePlanningUsageQuery,
  useProcessRoutePlanningMutation,
  useRecordRoutePlanningUsageMutation,
  useSubmitRoutePlanningMutation,
  useSearchDriversLazyQuery
} from "../generated/graphql";

import {AppToasterTopLeft, showSimpleSuccessToast} from "../utils/toasters/toaster";
import {AddressType} from "../components/route-planning/types/types";
import {getDriverAddressText} from "../utils/General";
import {useAssignmentDispatch} from "../providers/AssignmentProdiver";
import AddressValidationContent, {
  AddressValidationItem
} from "../components/route-planning/validation-content/AddressValidationContent";
import log from "loglevel";
import {useDispatchGroupContext} from "../providers/DispatchGroupProvider";
import {extractSimplePref, usePreferenceContext} from "../providers/PreferenceProvider";
import UnsupportedJobTypesContent, {
  UnsupportedJobTypesContentProps
} from "../components/route-planning/validation-content/UnssportedJobTypesContent";
import {AgGridReact} from "@ag-grid-community/react";
import {AgJob} from "../components/job/JobPanel";
import TimeWindowValidationContent from "../components/route-planning/validation-content/TimeWindowValidationContent";
import {useFeatureFlagContext} from "../providers/FeatureFlagProvider";
import UnassignedJobsContent from "../components/route-planning/validation-content/UnassignedJobsContent";
import UnassignedManifestJobsContent from "../components/route-planning/validation-content/UnassignedManifestJobsContent";
import LateManifestStopsContent from "../components/route-planning/validation-content/LateManifestStopsContent";
import {
  generateGraphhopperAssignmentDetails,
  getUnassignedManifestJobIds
} from "../components/route-planning/utils/RoutePlanning.utils";
import {StyledRadioGroup} from "../components/common/StyledRadioGroup";
import {OptimizationObjective} from "../components/settings/enums/RoutePlanning";
import {combineDatesAndTime} from "../utils/DayjsUtils";
import {DEFAULT_DWELL_MINUTES} from "../components/settings/CompanySettings/common/constants";
import RoutePlanningUsageBanner from "../components/route-planning/RoutePlanningUsageBanner";
import PlanningResult from "../components/route-planning/PlanningResult";

const RoutePlanningView = () => {
  const {
    routePlanningWithBothStops: routePlanningWithBothStopsEnabled,
    etaFromOptimization: isEtaFromOptimizationEnabled,
    routePlanningMap: routePlanningMapEnabled,
    itineraryPlanningSkills: itineraryPlanningSkillsEnabled,
    existingStopsIP: existingStopsIPEnabled
  } = useFeatureFlagContext();

  const [driversTab, setDriversTab] = useState<"new" | "existing">("new");
  const {loading} = useDispatchRulesDataContext();
  const state = useRoutePlanningContext();
  const {
    selectedJobs,
    selectedDrivers,
    allowMovingExistingWork,
    manifestStopsMap,
    dwellTime,
    optimizationObjective,
    currentStep,
    isLoading,
    originalResult
  } = state;
  const selectedNewDrivers = useMemo(() => selectedDrivers.filter((d) => !d.manifestDriverId), [selectedDrivers]);
  const selectedManifestDrivers = useMemo(() => selectedDrivers.filter((d) => !!d.manifestDriverId), [selectedDrivers]);
  const dispatch = useRoutePlanningDispatch();
  const assignmentStateDispatch = useAssignmentDispatch();
  const {isDispatchGroupLoaded} = useDispatchGroupContext();
  const {isUserPreferencesLoaded, tenantPreferences} = usePreferenceContext();
  const [fetchDrivers] = useSearchDriversLazyQuery();
  const [jobsGridRef, setJobsGridRef] = useState<React.RefObject<AgGridReact<AgJob>> | null>(null);
  const [showOptimizationObjectiveSelection, setShowOptimizationObjectiveSelection] = useState(false);
  const [isOptimizationObjectiveError, setIsOptimizationObjectiveError] = useState(false);

  const driversInfoRef = useRef<DriversInformationRef>(null);
  const alertRef = React.useRef<IAlertRef>(null);

  // DP-3110: back-navigation guard for stage 3 (the map).
  const history = useHistory();
  const [showBackConfirm, setShowBackConfirm] = useState(false);
  const backUnblockRef = useRef<(() => void) | null>(null);
  // DP-3202: ref to break circular dependency between handleError → handleSubmit
  const handleSubmitRef = useRef<() => Promise<boolean>>();
  const [alertProps, setAlertProps] = useState<{
    title: string;
    message: React.ReactNode;
    icon?: React.JSX.Element;
    onCancelCallback?: () => void;
    onConfirmCallback?: () => void | Promise<void>;
  }>({
    title: "",
    message: "",
    icon: undefined,
    onCancelCallback: undefined,
    onConfirmCallback: undefined
  });

  const [submitResponse, setSubmitResponse] = useState<SubmitRoutePlanningResponse>();
  const [submittedInput, setSubmittedInput] = useState<SubmitRoutePlanningInput>();
  const [isPolling, setIsPolling] = useState(false);
  const [planningStatus, setPlanningStatus] = useState<"success" | "failure">();
  const [showPlanningResult, setShowPlanningResult] = useState(false);
  const [isCreatingRoutes, setIsCreatingRoutes] = useState(false);
  const [modifiedDetails, setModifiedDetails] = useState<{
    assignmentDetails: AssignmentDetailWithEndDateTime[];
    isModified: boolean;
    hasRoutes: boolean;
    unassignedJobIds: number[];
  } | null>(null);
  const planningResultResetRef = useRef<(() => void) | null>(null);

  const [submitRoutePlanning] = useSubmitRoutePlanningMutation();
  const [processRoutePlanning] = useProcessRoutePlanningMutation();

  const [, {data: routePlanningResultData, startPolling, stopPolling}] = useGetRoutePlanningResultLazyQuery({
    variables: {
      input: {
        responseId: submitResponse ? submitResponse.responseId! : ""
      }
    }
  });

  // Freemium usage (DP-3062). Banner + submit gating are driven by this.
  const {data: usageData, refetch: refetchUsage} = useGetRoutePlanningUsageQuery({});
  const usage = usageData?.getRoutePlanningUsage;
  const [recordRoutePlanningUsage] = useRecordRoutePlanningUsageMutation();

  // Banner is shown by default on entering the view; the banner component itself
  // renders nothing when the tenant is unlimited / usage hasn't loaded.
  const [isUsageBannerOpen, setIsUsageBannerOpen] = useState(true);
  // True while the pre-validation usage refresh on a Next click is in flight, so
  // the Next button can show a spinner (DP-3062).
  const [isCheckingUsage, setIsCheckingUsage] = useState(false);
  // Bumped whenever the user attempts a blocked action while out of plans; drives
  // the banner's attention pulse (replaces the toast).
  const [bannerAttentionTick, setBannerAttentionTick] = useState(0);

  // Surface the out-of-plans banner: open it and replay its attention pulse.
  const flagOutOfPlans = useCallback(() => {
    setIsUsageBannerOpen(true);
    setBannerAttentionTick((tick) => tick + 1);
  }, []);

  // A plan "makes it to the map" → count it (DP-3062). Idempotent per responseId
  // on the backend; safe to call on full success and on skip-failed-and-continue.
  const recordUsage = useCallback(() => {
    const responseId = submitResponse?.responseId;
    if (!responseId) {
      return;
    }
    void recordRoutePlanningUsage({variables: {input: {responseId}}}).catch((error) => {
      log.error("Failed to record route planning usage:", error);
    });
  }, [recordRoutePlanningUsage, submitResponse]);

  // Refresh the weekly usage and re-surface the banner (DP-3062). Called on every
  // navigation click — the count can change out from under us (another user
  // consumed a plan, or an admin changed the company limit). Failures are
  // non-fatal: the server still enforces the quota via LIMIT_REACHED.
  const refreshUsageAndShowBanner = useCallback(() => {
    refetchUsage().catch((error) => {
      log.error("Failed to refresh route planning usage:", error);
    });
    setIsUsageBannerOpen(true);
  }, [refetchUsage]);

  const allowDismissFailedJobs = extractSimplePref(
    tenantPreferences,
    TenantPreferences.dismissFailedJobsRoutePlanning,
    true // DP-3166: unset companies default to "Yes"
  ).value;

  const handleSetGridRef = useCallback((ref: React.RefObject<AgGridReact<AgJob>>) => {
    setJobsGridRef(ref);
  }, []);

  useEffect(() => {
    if (isUserPreferencesLoaded) {
      dispatch({
        type: RoutePlanningActionType.UPDATE_DWELL_TIME,
        payload: extractSimplePref(
          tenantPreferences,
          TenantPreferences.assumedOptimizationDwellTime,
          DEFAULT_DWELL_MINUTES
        ).value as number
      });
    }
    // Only run once when preferences are loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserPreferencesLoaded]);

  const handleStartPolling = useCallback(() => {
    startPolling(500);
    setIsPolling(true);
  }, [startPolling]);

  const handleStopPolling = useCallback(() => {
    if (isPolling) {
      stopPolling();
      setIsPolling(false);
    }
  }, [isPolling, stopPolling]);

  const handleErrorScroll = useCallback(() => {
    const firstErrorRow = document.querySelector("[id^='error-row-']");
    if (firstErrorRow && typeof firstErrorRow.scrollIntoView === "function") {
      firstErrorRow.scrollIntoView({behavior: "smooth", block: "center"});
    }
  }, []);

  const handleUnselectJobs = useCallback(
    (unSupportedJobIds: number[]) => {
      dispatch({
        type: RoutePlanningActionType.SET_SELECTED_JOBS,
        payload: selectedJobs.filter((job) => !unSupportedJobIds.includes(job.jobId))
      });
      jobsGridRef?.current?.api.getSelectedNodes().forEach((node) => {
        if (unSupportedJobIds.includes(node.data!.jobId)) {
          node.setSelected(false);
        }
      });
    },
    [dispatch, jobsGridRef, selectedJobs]
  );

  const handleOptimizationObjectiveChange = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      const value = (event.target as HTMLInputElement).value as OptimizationObjective;
      dispatch({
        type: RoutePlanningActionType.UPDATE_OPTIMIZATION_OBJECTIVE,
        payload: value
      });
      setIsOptimizationObjectiveError(false);
    },
    [dispatch]
  );

  const handlePrevious = useCallback(() => {
    setShowPlanningResult(false);
    dispatch({type: RoutePlanningActionType.GO_TO_PREVIOUS_STEP});
    handleStopPolling();
    refreshUsageAndShowBanner();
  }, [dispatch, handleStopPolling, refreshUsageAndShowBanner]);

  const handleGoToStep = useCallback(
    (step: number) => {
      dispatch({type: RoutePlanningActionType.GO_TO_STEP, payload: step});
    },
    [dispatch]
  );

  const resetAlert = useCallback(() => {
    setAlertProps({title: "", message: "", icon: undefined, onCancelCallback: undefined});
  }, []);

  const handleResetAll = useCallback(() => {
    setShowPlanningResult(false);
    setPlanningStatus(undefined);
    dispatch({
      type: RoutePlanningActionType.RESET_ALL,
      payload: {
        dwellTime: extractSimplePref(
          tenantPreferences,
          TenantPreferences.assumedOptimizationDwellTime,
          DEFAULT_DWELL_MINUTES
        ).value as number
      }
    });
    jobsGridRef?.current?.api.deselectAll();
    driversInfoRef.current?.reset();
    handleGoToStep(1);
    refreshUsageAndShowBanner();
  }, [dispatch, handleGoToStep, jobsGridRef, tenantPreferences, refreshUsageAndShowBanner]);

  const handleCreateRoutes = useCallback(
    (allAssignmentDetails: AssignmentDetailWithEndDateTime[], metrics: RoutePlanningMetricsInput) => {
      setIsCreatingRoutes(true);

      // DP-3202: Exclude untouched manifests — they should not be sent to
      // processRoutePlanning since no changes were made. Compare ALL stops
      // (including completed) against the original manifest sequence.
      const assignmentDetails = allAssignmentDetails.filter((item) => {
        const mdId = item.submittedDriver.manifestDriverId;
        if (mdId == null) return true; // new drivers always included
        const originalStops = manifestStopsMap[mdId] ?? [];
        const originalStopIds = new Set(originalStops.map((s) => s.jobStopId));
        const originalSequence = originalStops
          .sort((a, b) => a.manifestSequence - b.manifestSequence)
          .map((s) => s.jobStopId);
        // If any stop is NOT from the original manifest → touched
        if (item.sequences.some((id) => !originalStopIds.has(id))) return true;
        // Compare full sequence including completed stops
        const isUntouched =
          item.sequences.length === originalSequence.length &&
          item.sequences.every((id, i) => id === originalSequence[i]);
        return !isUntouched;
      });

      // DP-3175: Build one PendingAssignment per route with the correct manifestDriverId.
      // For existing manifests, use their manifestDriverId directly.
      // For new drivers, use driverId as a temporary identifier (the pipeline Lambda
      // will publish real manifestDriverId once the manifest is created).
      const pendingActions = assignmentDetails.map((item) => ({
        jobIds: item.jobIds,
        jobStopIds: [] as number[],
        manifestDriverId: item.submittedDriver.manifestDriverId ?? item.submittedDriver.driverId,
        user: "",
        assignmentDateTime: Date.now(),
        status: 0, // PENDING
        updatedAt: Date.now()
      }));

      assignmentStateDispatch({
        type: "ClearSelectionAndSetPendingAction",
        payload: pendingActions
      });

      processRoutePlanning({
        variables: {
          input: {
            // The backend doesn't need endDateTime, it's only used for displaying in the map, so we must omit it here.
            // DP-3175 B08: Only filter out NEW drivers with no jobs. Existing manifests
            // emptied by moving all jobs must still be sent so the backend can categorize
            // their stops as MOVED and reassign them to the target routes.
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            assignmentDetails: assignmentDetails
              .filter((item) => item.jobIds.length > 0 || item.submittedDriver.manifestDriverId != null)
              .map(({endDateTime, ...rest}) => rest),
            responseId: submitResponse?.responseId,
            metrics: metrics
          }
        },
        onCompleted: (data) => {
          setIsCreatingRoutes(false);
          if (data.processRoutePlanning?.status === "SUCCESSED") {
            setPlanningStatus("success");
            showSimpleSuccessToast("Itinerary Planning Successful", data.processRoutePlanning?.message ?? "");
          } else {
            AppToasterTopLeft.show({
              message:
                data.processRoutePlanning?.message ||
                "An error occurred while processing the results. Please try again later.",
              intent: Intent.WARNING
            });
            setPlanningStatus("failure");
          }
        },
        onError: () => {
          setIsCreatingRoutes(false);
        }
      });
    },
    [assignmentStateDispatch, processRoutePlanning, originalResult, manifestStopsMap, submitResponse]
  );

  const handleResult = useCallback(
    (result: GetRoutePlanningResultResponse, dismissJobIds: number[] = []) => {
      // The plan reaches the map here — full success, or skip-failed-and-continue
      // (handleResult is called from the dismiss confirm). Count it (DP-3062).
      recordUsage();
      // DP-3121: optimization ETAs (incl. the wait-for-early-time-window
      // clamp) are computed by the route-planning lambda and returned on the
      // poll response — the frontend only consumes them.
      const startDate = selectedDrivers[0].startDate!.format("YYYY-MM-DD");
      const assignmentDetails = generateGraphhopperAssignmentDetails(
        result.solution!,
        startDate,
        dismissJobIds,
        submittedInput!,
        isEtaFromOptimizationEnabled,
        result.routeEtas
      );

      log.debug("RP:assignmentDetails", assignmentDetails);
      const metrics: RoutePlanningMetricsInput = {
        routesCount: result?.solution?.routes?.length,
        processingTime: result?.processing_time,
        waitingTimeInQueue: result?.waiting_time_in_queue,
        wasModified: false
      };
      log.debug("RP:metrics", metrics);

      if (routePlanningMapEnabled) {
        dispatch({
          type: RoutePlanningActionType.SET_ORIGINAL_RESULT,
          payload: {result, assignmentDetails, metrics}
        });
        setShowPlanningResult(true);
      } else {
        handleCreateRoutes(assignmentDetails, metrics);
      }
    },
    [
      selectedDrivers,
      submittedInput,
      isEtaFromOptimizationEnabled,
      routePlanningMapEnabled,
      dispatch,
      handleCreateRoutes,
      recordUsage
    ]
  );

  const handleError = useCallback(
    (type: RoutePlanningErrorType, details?: any) => {
      switch (type) {
        case RoutePlanningErrorType.OPTIMIZATION_OBJECTIVE_REQUIRED:
          setIsOptimizationObjectiveError(true);
          return;

        case RoutePlanningErrorType.SELECTION_REQUIRED:
          setAlertProps({
            title: "WARNING",
            message: details,
            icon: <WarningIcon />
          });
          break;

        case RoutePlanningErrorType.UNSUPPORTED_JOBS: {
          const typedDetails = details as UnsupportedJobTypesContentProps;
          const unSupportedJobIds = [
            ...typedDetails.multiplePickupsAndDeliveriesJobs,
            ...typedDetails.bothStopJobs
          ].map((job) => job.jobId);

          setAlertProps({
            title: "CERTAIN JOB TYPES NOT YET SUPPORTED",
            message: <UnsupportedJobTypesContent {...(details as UnsupportedJobTypesContentProps)} />,
            icon: <WarningIcon />,
            onCancelCallback: () => {
              handleUnselectJobs(unSupportedJobIds);
            }
          });
          break;
        }

        case RoutePlanningErrorType.MISSING_TIME_WINDOW: {
          setAlertProps({
            title: "MISSING TIME WINDOW",
            message: <TimeWindowValidationContent jobs={details} />,
            icon: <WarningIcon />
          });
          break;
        }

        case RoutePlanningErrorType.STOPS_ADDRESSES_INVALID: {
          const invalidJobIds = Array.from(
            new Set(
              (details as AddressValidationItem[]).map((item) => item.jobId).filter((id): id is number => id != null)
            )
          );
          setAlertProps({
            title: "INVALID STOP ADDRESS",
            message: (
              <AddressValidationContent type="stop-address" items={details} isGrouped={true} showGroupLinks={true} />
            ),
            icon: <WarningIcon />,
            onCancelCallback: () => handleUnselectJobs(invalidJobIds)
          });
          break;
        }

        case RoutePlanningErrorType.MANIFEST_STOPS_PROJECTED_LATE: {
          const lateManifests = details as Array<{manifestDriverId: number; driverName: string}>;
          setAlertProps({
            title: "MANIFEST STOPS PROJECTED LATE",
            message: <LateManifestStopsContent lateManifests={lateManifests} />,
            icon: <WarningIcon />,
            onCancelCallback: () => {
              handleGoToStep(1);
            },
            onConfirmCallback: () => {
              // DP-3202: Remove flagged manifests automatically and re-submit
              const lateIds = new Set(lateManifests.map((m) => m.manifestDriverId));
              for (const m of lateManifests) {
                dispatch({type: RoutePlanningActionType.REMOVE_MANIFEST, payload: m.manifestDriverId});
              }
              // Check if enough drivers/jobs remain after removal
              const remainingDrivers = selectedDrivers.filter((d) => !lateIds.has(d.manifestDriverId!));
              if (remainingDrivers.length < 2) {
                const message = "At least 2 drivers or manifests must be selected in order to continue";
                handleGoToStep(1);
                setTimeout(() => {
                  setAlertProps({
                    title: "WARNING",
                    message,
                    icon: <WarningIcon />
                  });
                  alertRef.current?.open();
                }, 0);
              } else {
                // Re-trigger the submit flow automatically (no need to click Next again).
                // Use setTimeout to let the REMOVE_MANIFEST dispatches settle in state first.
                setTimeout(() => {
                  dispatch({
                    type: RoutePlanningAsyncActionType.GO_TO_NEXT_STEP_ASYNC,
                    payload: {successCallback: handleSubmitRef.current, errorCallback: handleError}
                  });
                }, 0);
              }
            }
          });
          break;
        }

        case RoutePlanningErrorType.DRIVER_INFORMATION_INVALID:
          handleErrorScroll();
          return;

        case RoutePlanningErrorType.DRIVER_START_END_LOCATION_INVALID:
          setAlertProps({
            title: "INVALID START/END LOCATION",
            message: <AddressValidationContent type="driver-address" items={details} isGrouped={true} />,
            icon: <WarningIcon />
          });
          break;

        case RoutePlanningErrorType.MANIFEST_JOBS_UNASSIGNED: {
          const manifestResult = details as GetRoutePlanningResultResponse;
          const unassignedManifestJobIds = getUnassignedManifestJobIds(
            manifestResult.solution?.unassigned?.details ?? []
          );
          setPlanningStatus("failure");
          setAlertProps({
            title: "MANIFEST JOBS UNASSIGNED",
            message: (
              <UnassignedManifestJobsContent
                unassignedManifestJobIds={unassignedManifestJobIds}
                manifestStopsMap={manifestStopsMap}
              />
            ),
            icon: <WarningIcon />,
            onCancelCallback: () => {
              setPlanningStatus(undefined);
              handleGoToStep(1);
            }
          });
          break;
        }

        case RoutePlanningErrorType.CAN_NOT_ASSIGN_JOBS: {
          const result = details as GetRoutePlanningResultResponse;
          const typedDetails = result.solution?.unassigned?.details ?? [];
          const errorJobIds = new Set(typedDetails.map((dt) => Number(dt.id?.split(":")[1])));

          // DP-3202: Check if any route would still have stops after dismissing
          // failed jobs. If dismissing would empty all routes, don't allow dismiss.
          const solutionRoutes = result.solution?.routes ?? [];
          const hasRouteWithNonFailedStops = solutionRoutes.some((route) => {
            const stopActivities = (route.activities ?? []).filter((a) => a.type !== "start" && a.type !== "end");
            if (stopActivities.length === 0) return false;
            // Check if any stop on this route is NOT from a failed job
            return stopActivities.some((a) => {
              const jobId = Number(a.id?.split(":")[1] ?? a.location_id?.split(":")[1]);
              return !errorJobIds.has(jobId);
            });
          });
          const isAllJobsFailed = !hasRouteWithNonFailedStops;
          const showDismissOption = allowDismissFailedJobs && !isAllJobsFailed;
          if (!showDismissOption) {
            setPlanningStatus("failure");
          }
          setAlertProps({
            title: "CAN NOT ASSIGN JOBS",
            message: (
              <UnassignedJobsContent
                details={typedDetails}
                showDismissOption={showDismissOption}
                selectedJobs={selectedJobs}
                manifestStopsMap={manifestStopsMap}
              />
            ),
            icon: <WarningIcon />,
            onCancelCallback: () => {
              setPlanningStatus(undefined);
              handleGoToStep(1);
            },
            onConfirmCallback: showDismissOption
              ? () => {
                  setPlanningStatus(undefined);
                  handleResult(result, Array.from(errorJobIds));
                }
              : undefined
          });
          break;
        }

        case RoutePlanningErrorType.INVALID_DATA: {
          const typedDetails = details as RoutePlanningUnassignedDetail[];
          setPlanningStatus("failure");
          setAlertProps({
            title: "UNABLE TO ROUTE PLANNING",
            message: (
              <UnassignedJobsContent
                details={typedDetails}
                selectedJobs={selectedJobs}
                manifestStopsMap={manifestStopsMap}
              />
            ),
            icon: <WarningIcon />,
            onCancelCallback: () => {
              setPlanningStatus(undefined);
              handleGoToStep(1);
            }
          });
          break;
        }

        default:
      }

      alertRef.current?.open();
    },
    [
      allowDismissFailedJobs,
      dispatch,
      handleErrorScroll,
      handleGoToStep,
      handleResult,
      handleUnselectJobs,
      manifestStopsMap,
      selectedDrivers,
      selectedJobs.length
    ]
  );

  const considerSkills =
    itineraryPlanningSkillsEnabled &&
    // DP-3166: unset companies default to "Yes"
    (extractSimplePref(tenantPreferences, TenantPreferences.considerSkillsRoutePlanning, true).value as boolean);

  const handleSubmit = useCallback(async () => {
    try {
      const pushDrivers: RoutePlanningDriverInput[] = await Promise.all(
        selectedDrivers.map(async (driver) => {
          const startTime = combineDatesAndTime(driver.startDate, driver.startTime, driver.startTimeZone).toISOString();

          return {
            driverId: driver.driverId as number,
            ...(driver.manifestDriverId ? {manifestDriverId: driver.manifestDriverId} : {}),
            startTime: startTime,
            startAddress: driver.startLocation!,
            startAddressPosition: await getAddressPositionWithCache(driver.startLocation!),
            endAddress: driver.endLocation,
            endAddressPosition: driver.endLocation ? await getAddressPositionWithCache(driver.endLocation) : undefined,
            skillsIds: (driver.skills ?? "")
              .split(",")
              .map((s: string) => parseInt(s.trim(), 10))
              .filter((id: number) => !isNaN(id))
          };
        })
      );

      const startDate = selectedDrivers[0].startDate?.format("YYYY-MM-DD");

      const submitData: SubmitRoutePlanningInput = {
        jobIds: selectedJobs.map((job) => job.jobId),
        drivers: pushDrivers,
        dwellTimeSeconds: dwellTime * 60,
        startDate: startDate,
        startAnyTime: false,
        objective: optimizationObjective,
        considerSkills: considerSkills,
        ...(selectedManifestDrivers.length > 0 && {
          allowMovingExistingWork: allowMovingExistingWork
        })
      };

      const res = await submitRoutePlanning({
        variables: {
          input: submitData
        }
      });

      if (res.data?.submitRoutePlanning?.status === "LIMIT_REACHED") {
        // Server-enforced weekly quota (DP-3062). Refresh usage so the banner
        // flips to the out-of-plans state, then surface it with the attention pulse.
        void refetchUsage();
        flagOutOfPlans();
        return false;
      } else if (res.data?.submitRoutePlanning?.errorsJson) {
        const errors = JSON.parse(res.data.submitRoutePlanning.errorsJson);
        handleError(RoutePlanningErrorType.INVALID_DATA, errors);
        return false;
      } else if (res?.data?.submitRoutePlanning?.responseId) {
        setSubmittedInput(submitData);
        setSubmitResponse(res.data.submitRoutePlanning);
        handleStartPolling();
        return true;
      }

      return false;
    } catch (error) {
      log.error("Error submitting route planning:", error);
      AppToasterTopLeft.show({
        message: "An error occurred while submitting the optimization request. Please try again later.",
        intent: Intent.WARNING
      });
      return false;
    }
  }, [
    dwellTime,
    handleError,
    handleStartPolling,
    optimizationObjective,
    selectedDrivers,
    selectedJobs,
    selectedManifestDrivers,
    allowMovingExistingWork,
    submitRoutePlanning,
    refetchUsage,
    flagOutOfPlans,
    considerSkills
  ]);

  handleSubmitRef.current = handleSubmit;

  const handleNext = useCallback(async () => {
    // Refresh usage before any validation (DP-3062). The count can move out from
    // under us between renders — another user consumed a plan, or an admin changed
    // the company limit — so we validate against the freshly fetched value. If the
    // refresh fails we fall back to the last-known usage; the server still enforces
    // the quota with LIMIT_REACHED, so navigation is never blocked on this call.
    let latestUsage = usage;
    setIsCheckingUsage(true);
    try {
      const {data: freshUsageData} = await refetchUsage();
      latestUsage = freshUsageData?.getRoutePlanningUsage ?? latestUsage;
    } catch (error) {
      log.error("Failed to refresh route planning usage:", error);
    } finally {
      setIsCheckingUsage(false);
    }
    const atPlanLimit = !!latestUsage && !latestUsage.isUnlimited && latestUsage.used >= latestUsage.limit;

    // Out of weekly plans (DP-3062): block forward navigation entirely — the user
    // can't advance from step 1 → 2 or submit from step 2. The Next button stays
    // enabled; instead we surface the banner with an attention pulse (re-opening it
    // if it was dismissed/hidden). The server also rejects with LIMIT_REACHED.
    if (atPlanLimit) {
      flagOutOfPlans();
      return;
    }

    switch (currentStep) {
      case 1: {
        // Pre-fetch home addresses for manifest drivers before moving to Step 2.
        // Manifest card data doesn't include driver address fields — fetch in batch
        // during the loading spinner so addresses are ready when Step 2 renders.
        const manifestDriversNeedingAddress = selectedManifestDrivers.filter((d) => !d.address && d.driverId);
        if (manifestDriversNeedingAddress.length > 0) {
          dispatch({type: RoutePlanningActionType.UPDATE_IS_LOADING, payload: true});
          const uniqueIds = Array.from(new Set(manifestDriversNeedingAddress.map((d) => d.driverId!)));
          try {
            const res = await fetchDrivers({
              variables: {filter: {or: uniqueIds.map((id) => ({driverId: {eq: id}}))}, limit: uniqueIds.length}
            });
            const items = (res.data?.searchDrivers?.items ?? []).filter(Boolean);
            const driverMap = new Map(items.map((d) => [d!.driverId, d!]));
            const updatedDrivers = selectedDrivers.map((d) => {
              if (!d.manifestDriverId) return d;
              const data = driverMap.get(d.driverId!);
              if (!data || d.address) return d;
              const updated = {...d, address: data.address, city: data.city, state: data.state, zip: data.zip};
              // Set startLocation for HOME type since it was null without address data
              if (d.startLocationType === AddressType.HOME) {
                updated.startLocation = getDriverAddressText(updated as any) || null;
              }
              return updated;
            });
            dispatch({type: RoutePlanningActionType.UPDATE_STATE, payload: {selectedDrivers: updatedDrivers}});
          } catch (e) {
            log.warn("Failed to fetch manifest driver addresses", e);
          }
        }
        dispatch({
          type: RoutePlanningAsyncActionType.GO_TO_NEXT_STEP_ASYNC,
          payload: {
            errorCallback: handleError,
            allowBothStops: routePlanningWithBothStopsEnabled,
            existingStopsEnabled: existingStopsIPEnabled
          }
        });
        break;
      }

      case 2: {
        dispatch({
          type: RoutePlanningAsyncActionType.GO_TO_NEXT_STEP_ASYNC,
          payload: {successCallback: handleSubmit, errorCallback: handleError}
        });
        break;
      }

      default:
        return;
    }
  }, [
    currentStep,
    dispatch,
    handleError,
    handleSubmit,
    routePlanningWithBothStopsEnabled,
    refetchUsage,
    usage,
    flagOutOfPlans,
    fetchDrivers,
    selectedManifestDrivers,
    selectedDrivers
  ]);

  useEffect(() => {
    if (tenantPreferences) {
      const optimizationObjective: OptimizationObjective = extractSimplePref(
        tenantPreferences,
        TenantPreferences.optimizationObjectives,
        OptimizationObjective.Both // DP-3113: unset companies default to "Both"
      ).value as OptimizationObjective;
      if (optimizationObjective === OptimizationObjective.Both) {
        setShowOptimizationObjectiveSelection(true);
      }
      dispatch({
        type: RoutePlanningActionType.UPDATE_OPTIMIZATION_OBJECTIVE,
        payload: optimizationObjective
      });
    }
  }, [dispatch, tenantPreferences]);

  useEffect(() => {
    if (isPolling && routePlanningResultData?.getRoutePlanningResult?.status === "finished") {
      handleStopPolling();
      const result = routePlanningResultData.getRoutePlanningResult!;
      const solution = result.solution;
      if (solution?.no_unassigned && solution.no_unassigned > 0) {
        const unassignedManifestJobIds = getUnassignedManifestJobIds(solution?.unassigned?.details ?? []);
        if (unassignedManifestJobIds.length > 0) {
          // DP-3202: When manifest stops are unassigned, show the manifest-specific
          // error (no dismiss). Regular job failures are hidden — the user must
          // resolve manifest issues first. Regular failures will surface on retry.
          handleError(RoutePlanningErrorType.MANIFEST_JOBS_UNASSIGNED, result);
        } else {
          handleError(RoutePlanningErrorType.CAN_NOT_ASSIGN_JOBS, result);
        }
      } else {
        // Full success → reaches the map; handleResult records the usage.
        handleResult(result);
      }
    }
  }, [
    routePlanningResultData,
    handleStopPolling,
    handleError,
    handleResult,
    isPolling,
    routePlanningMapEnabled,
    existingStopsIPEnabled,
    selectedManifestDrivers.length,
    recordUsage
  ]);

  useEffect(() => {
    return () => {
      log.debug("Cleaning up RoutePlanningView cache");
      dispatch({
        type: RoutePlanningActionType.RESET_CACHE
      });
    };
  }, [dispatch]);

  // DP-3110: on stage 3 (the map), a browser back / trackpad swipe-back / Alt+← / Cmd+← is a
  // history POP that leaves Itinerary Planning and discards the in-progress plan. Intercept it
  // with a styled confirmation instead of losing everything silently. Armed only on step 3 and
  // scoped to POP, so legitimate programmatic (PUSH) navigation and the in-app Previous/Next
  // (reducer actions, no history) are never affected. `history.block` returning false blocks the
  // transition without the native getUserConfirmation, so we don't touch the global router (the
  // Settings page's <Prompt> stays as-is). react-router-dom v5.
  useEffect(() => {
    if (currentStep !== 3) return;
    const unblock = history.block((_location, action) => {
      if (action !== "POP") return;
      setShowBackConfirm(true);
      return false;
    });
    backUnblockRef.current = unblock;
    return () => {
      unblock();
      backUnblockRef.current = null;
    };
  }, [currentStep, history]);

  const handleConfirmBack = () => {
    // Remove the block first so re-issuing the back isn't caught again, then leave the flow
    // (existing RESET_CACHE unmount cleanup discards the plan state).
    backUnblockRef.current?.();
    backUnblockRef.current = null;
    setShowBackConfirm(false);
    history.goBack();
  };

  // Cancel: just close the dialog. The block stays armed, so a second back re-prompts.
  const handleCancelBack = () => setShowBackConfirm(false);

  return (
    <LoadingSpinner loading={loading || !isDispatchGroupLoaded || !isUserPreferencesLoaded}>
      <Container data-testid="route-planning-view">
        <Body>
          <ContentColumn>
            <RoutePlanningUsageBanner
              usage={usage}
              open={isUsageBannerOpen && currentStep !== 3}
              onClose={() => setIsUsageBannerOpen(false)}
              attentionTick={bannerAttentionTick}
            />
            <Slider style={{transform: `translateX(-${(currentStep - 1) * 100}%)`}}>
              <Step>
                {showOptimizationObjectiveSelection && (
                  <>
                    <StyledRadioGroup
                      label="What is the objective of the optimization?"
                      onChange={handleOptimizationObjectiveChange}
                      selectedValue={optimizationObjective}
                      horizontal
                      style={{width: "fit-content", fontSize: "16px"}}
                    >
                      <Radio label="Minimize Time to Complete" value={OptimizationObjective.MinCompletionTime} />
                      <Radio label="Divide Work Evenly" value={OptimizationObjective.BalanceWorks} />
                    </StyledRadioGroup>
                    {isOptimizationObjectiveError && (
                      <Callout intent={Intent.DANGER}>Please select an optimization objective.</Callout>
                    )}
                  </>
                )}
                <Grid>
                  <Section data-testid="jobs-section">
                    <SectionTitle>Jobs</SectionTitle>
                    <JobsGrid setGridRef={handleSetGridRef} />
                  </Section>
                  <Section data-testid="drivers-section">
                    <SectionTitle>Drivers</SectionTitle>
                    {existingStopsIPEnabled && (
                      <SegmentedToggle>
                        <SegmentButton
                          data-testid="ip-tab-new-drivers"
                          active={driversTab === "new"}
                          onClick={() => setDriversTab("new")}
                        >
                          New Drivers
                          {selectedNewDrivers.length > 0 && <CountBadge>{selectedNewDrivers.length}</CountBadge>}
                        </SegmentButton>
                        <SegmentButton
                          data-testid="ip-tab-existing-manifests"
                          active={driversTab === "existing"}
                          onClick={() => setDriversTab("existing")}
                        >
                          Existing Manifests
                          {selectedManifestDrivers.length > 0 && (
                            <CountBadge>{selectedManifestDrivers.length}</CountBadge>
                          )}
                        </SegmentButton>
                      </SegmentedToggle>
                    )}
                    {existingStopsIPEnabled && driversTab === "existing" && (
                      <AllowMovingSwitch
                        data-testid="ip-allow-moving-work-toggle"
                        checked={allowMovingExistingWork}
                        onChange={() =>
                          dispatch({
                            type: RoutePlanningActionType.SET_ALLOW_MOVING_WORK,
                            payload: !allowMovingExistingWork
                          })
                        }
                        label="Allow moving work from existing drivers"
                      />
                    )}
                    {/* Both panels stay mounted (display:none) to preserve state across tab switches */}
                    <TabPanel visible={driversTab === "new" || !existingStopsIPEnabled}>
                      <DriversGrid />
                    </TabPanel>
                    {existingStopsIPEnabled && (
                      <TabPanel visible={driversTab === "existing"}>
                        <IPManifestSelector />
                      </TabPanel>
                    )}
                  </Section>
                </Grid>
              </Step>
              <Step>
                <CenterDiv>
                  <Section
                    data-testid="drivers-information-section"
                    style={{width: "70vw", minWidth: "800px", padding: "0 16px"}}
                  >
                    <SectionTitle>Drivers</SectionTitle>
                    <DriversInformation ref={driversInfoRef} />
                  </Section>
                </CenterDiv>
              </Step>
              <Step style={showPlanningResult && !planningStatus ? {padding: 0} : undefined}>
                {showPlanningResult && !planningStatus ? (
                  <PlanningResult onAssignmentDetailsChange={setModifiedDetails} resetRef={planningResultResetRef} />
                ) : (
                  <CenterDiv data-testid="route-planning-status-screen">
                    {planningStatus === "success" && (
                      <FlexDiv column>
                        <Icon icon="tick-circle" intent="success" size={40}></Icon>
                        <StatusText> Itinerary Planning Successfully!</StatusText>
                      </FlexDiv>
                    )}
                    {planningStatus === "failure" && (
                      <FlexDiv column>
                        <Icon icon="error" intent="danger" size={40} />
                        <StatusText> Itinerary Planning Failed!</StatusText>
                      </FlexDiv>
                    )}
                    {!planningStatus && (
                      <FlexDiv column>
                        <StatusText>Itinerary Planning in Progress...</StatusText>
                        <ProgressBar intent="primary" style={{width: "300px"}} />
                        {/* DP-3112: context blurb under the loading bar. The bold phrase reflects the
                            selected optimization objective (always concrete here — Both is blocked at stage 1). */}
                        <CalculatingBlurb data-testid="route-planning-calculating-blurb">
                          <p>
                            Depending on your inputs, particularly how many stops &amp; drivers, this may take anywhere
                            from 30 seconds to ~5 minutes. During this process we are analyzing each job and every stop
                            on it for the location, the time window requirements, and more.
                          </p>
                          <p>
                            We are also analyzing the drivers start and end locations, start time, dwell time, a maximum
                            8 hour shift for each driver, and driver skills requirements if relevant.
                          </p>
                          <p>
                            With these factors in mind an output will be given that{" "}
                            <strong>
                              {optimizationObjective === OptimizationObjective.BalanceWorks
                                ? "divides work evenly as efficiently as possible"
                                : "minimizes time in transit"}
                            </strong>{" "}
                            that you can review, adjust and confirm with the aid of a map on the next page.
                          </p>
                        </CalculatingBlurb>
                      </FlexDiv>
                    )}
                  </CenterDiv>
                )}
              </Step>
            </Slider>
          </ContentColumn>
        </Body>
        <Footer>
          {showPlanningResult && !planningStatus ? (
            <>
              <NavigateButton
                data-testid="cancel-planning-result-btn"
                onClick={() => {
                  setShowPlanningResult(false);
                  setModifiedDetails(null);
                  handleGoToStep(2);
                  refreshUsageAndShowBanner();
                }}
              >
                Cancel
              </NavigateButton>
              <NavigateButton
                data-testid="create-routes-btn"
                intent="primary"
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onClick={() => {
                  const isModified = modifiedDetails?.isModified ?? false;
                  const assignmentDetails = isModified
                    ? modifiedDetails!.assignmentDetails
                    : originalResult!.assignmentDetails;
                  const metrics: RoutePlanningMetricsInput = {
                    ...originalResult!.metrics,
                    wasModified: isModified
                  };
                  handleCreateRoutes(assignmentDetails, metrics);
                }}
                style={{width: "120px"}}
                disabled={isCreatingRoutes || (modifiedDetails != null && !modifiedDetails.hasRoutes)}
              >
                {isCreatingRoutes ? <LoadingSpinner size={20} /> : "Create Routes"}
              </NavigateButton>
            </>
          ) : (
            <>
              <NavigateButton
                data-testid="previous-btn"
                disabled={currentStep === 1 || isLoading || isPolling}
                onClick={planningStatus === "success" ? handleResetAll : handlePrevious}
              >
                {planningStatus === "success" ? "New Planning" : "Previous"}
              </NavigateButton>
              {currentStep < 3 && (
                <NavigateButton
                  data-testid="next-btn"
                  intent="primary"
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onClick={handleNext}
                  style={{width: "52px"}}
                  disabled={isLoading || isCheckingUsage}
                >
                  {isLoading || isCheckingUsage ? <LoadingSpinner size={20} /> : "Next"}
                </NavigateButton>
              )}
            </>
          )}
        </Footer>
        <Alert
          ref={alertRef}
          message={alertProps.message}
          icon={alertProps.icon}
          title={alertProps.title}
          onCancel={alertProps.onCancelCallback}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onConfirm={alertProps.onConfirmCallback}
          clearState={resetAlert}
        />
        <ConfirmationDialog
          testIdPrefix="route-planning-back"
          isVisible={showBackConfirm}
          header="Go back?"
          body="Are you sure you want to go back? All progress will be lost"
          yesText="Yes"
          noText="Cancel"
          handleYesClicked={handleConfirmBack}
          handleNoClicked={handleCancelBack}
          handleClose={handleCancelBack}
        />
      </Container>
    </LoadingSpinner>
  );
};

export default RoutePlanningView;

const Container = styled.div`
  font-family: "Roboto", sans-serif;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${COLORS.BG_GRAY};
`;

const Body = styled.div`
  display: flex;
  height: 100%;
  width: 100%;

  overflow: hidden;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  height: 56px;
  background-color: ${COLORS.WHITE};
`;

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-width: 0;
  overflow: hidden;
`;

const Slider = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  width: 100%;
  transition: transform 0.5s ease-in-out;
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 16px;
  height: 100%;
  width: 100%;
  padding: 16px 16px 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  grid-gap: 32px;
  flex: 1;
  min-height: 0;
  width: 100%;
`;

const AllowMovingSwitch = styled(Switch)`
  margin-bottom: 12px;
  width: fit-content;
  &.bp5-control.bp5-switch input:checked ~ .bp5-control-indicator {
    background-color: ${COLORS.GREEN_SUCCESS};
  }
`;

/** Keeps both DriversGrid and IPManifestSelector mounted; hides the inactive tab with CSS. */
const TabPanel = styled.div<{visible: boolean}>`
  display: ${(p) => (p.visible ? "flex" : "none")};
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const SegmentedToggle = styled.div`
  width: fit-content;
  display: flex;
  background: #fff;
  border: 1px solid ${COLORS.COLOR_BORDER};
  padding: 4px;
  border-radius: 8px;
  gap: 4px;
  margin-bottom: 12px;
`;

const SegmentButton = styled.button<{active?: boolean}>`
  border: none;
  background: ${(p) => (p.active ? COLORS.BLUE : "transparent")};
  color: ${(p) => (p.active ? "#fff" : "#4b5160")};
  padding: 6px;
  height: 24px;
  min-width: 85px;
  border-radius: 6px;
  font-size: 12.5px;
  font-weight: ${(p) => (p.active ? 600 : 500)};
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  &:hover {
    background: ${(p) => (p.active ? COLORS.BLUE : "#f4f5f7")};
  }
`;

const CountBadge = styled.span`
  background: ${COLORS.GREEN_SUCCESS};
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
`;

const Section = styled.div`
  display: flex;
  overflow: hidden;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const SectionTitle = styled.p`
  font-size: 18px;
`;

const NavigateButton = styled(Button)`
  padding: 4px 12px;
  border-radius: 8px;
  box-shadow: none !important;
  height: 32px;

  &.bp5-button.bp5-intent-primary:not(.bp5-disabled) {
    background: ${COLORS.BG_GRADIENT_DARK_BLUE};
    color: ${COLORS.WHITE};
  }

  &.bp5-button:not([class*="bp5-intent-"]):not(.bp5-disabled) {
    color: ${COLORS.BLUE};
    background: ${COLORS.BRIGHT_BLUE};
  }

  :hover:not(.bp5-disabled) {
    opacity: 0.95;
  }

  :active:not(.bp5-disabled) {
    opacity: 0.8;
  }
`;

const CenterDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

const FlexDiv = styled.div<{column?: boolean}>`
  display: flex;
  align-items: center;
  flex-direction: ${(props) => (props.column ? "column" : "row")};
  gap: 8px;
`;

const StatusText = styled(Text)`
  font-weight: 500;
  font-size: 16px;
`;

// DP-3112: expectation-setting copy shown under the loading bar while a plan is calculating.
const CalculatingBlurb = styled.div`
  max-width: 640px;
  margin-top: 16px;
  text-align: center;
  font-size: 14px;
  line-height: 1.5;
  color: ${COLORS.TEXT_GRAY};

  p {
    margin: 0;
    /* Avoid a single word wrapping alone on the last line (e.g. "page.") for the longer objective phrase. */
    text-wrap: pretty;
  }
  p + p {
    margin-top: 12px;
  }
  strong {
    font-weight: 600;
    color: ${COLORS.TEXT_BLACK};
  }
`;
